/* eslint-disable no-unused-expressions */
import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from 'react-toastify';
import { API_TOKEN, API_URL, HYPERSCOUT_API_URL, HYPERSCOUT_BACKEND_URL } from "../../Config/secrets";
import { decryptContactsData } from "../../Utils/contact-decrpytion";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  totalUsers: 0,
  allUsers: {},
  selectedUser: null,
};

const slice = createSlice({
  name: "userManagement",
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    setSelectedUser(state, action) {
      state.selectedUser = action.payload;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    // GET USERS
    getAllUsersSuccess(state, action) {
      state.error = null;
      state.totalUsers = action.payload.total;
      state.allUsers = {
        ...state.allUsers,
        [action.payload.page]: action.payload.data,
      };
      state.isLoading = false;
    },
    resetUserManagement: () => initialState,
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { resetUserManagement, setSelectedUser } = slice.actions;

// ----------------------------------------------------------------------

const header = {
  Authorization: API_TOKEN,
  source: 'mobile'
};

export function getAllUsers(page = 1, query = '', take = 10, hideLoader = false) {
  return async (dispatch) => {
    try {
      if (!hideLoader) {
        dispatch(slice.actions.startLoading());
      }

      let payload = { skip: page, take };

      if (query) payload.query = query;

      const response = await axios.post(`${API_URL}/user-management/get-users`, payload, {
        headers: header,
      });

      console.log('GET USERS RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        const users = response?.data?.data?.data || [];
        const decryptedUsers = await Promise.all(
          users.map(async (user) => ({
            ...user,
            phone: user.phone ? await decryptContactsData(user.phone).catch(() => '--') : '--',
          }))
        );
        dispatch(
          slice.actions.getAllUsersSuccess({
            page,
            total: response?.data?.data?.count || 0,
            data: decryptedUsers,
          })
        );
        return decryptedUsers;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message));
        toast.error(response?.data?.message || "Failed to fetch users", { autoClose: 3000 });
      }
    } catch (error) {
      console.error('GET USERS ERROR', error);
      toast.error(error?.message || "Failed to fetch users", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function toggleUserStatus(userId, currentStatus) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/user-management/toggle-user-active`,
        { userId, active: !currentStatus },
        { headers: header }
      );

      console.log('TOGGLE USER STATUS RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "User status updated", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to update user status", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('TOGGLE USER STATUS ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to update user status", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function deleteUsers(userIds) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/user-management/delete-user`,
        { userIds },
        { headers: header }
      );

      console.log('DELETE USERS RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "User(s) deleted successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to delete user(s)", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('DELETE USERS ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete user(s)", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function toggleCrmFeature(userId, status) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${API_URL}/user-management/toggle-permission-feature`,
        { userId, permissionType: 'crm', status },
        { headers: header }
      );

      console.log('TOGGLE CRM FEATURE RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "CRM feature updated", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to update CRM feature", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('TOGGLE CRM FEATURE ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to update CRM feature", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function toggleUserAdmin(userId, isAdmin) {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${API_URL}/user-management/toggle-user-admin`,
        { userId, isAdmin },
        { headers: header }
      );

      console.log('TOGGLE USER ADMIN RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || 'Organization admin updated', { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || 'Failed to update organization admin', { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('TOGGLE USER ADMIN ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update organization admin', { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function resetUserPassword(userId, newPassword) {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${API_URL}/user-management/reset-user-password`,
        {
          userId: Number(userId),
          new_password: newPassword,
        },
        { headers: header }
      );

      console.log('RESET USER PASSWORD RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || 'User password reset successfully', { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || 'Failed to reset user password', { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('RESET USER PASSWORD ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to reset user password', { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function inviteOrganization(inviteData) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${HYPERSCOUT_BACKEND_URL}/auth/register`,
        inviteData,
        { headers: header }
      );

      console.log('INVITE USER RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Invitation sent successfully", { autoClose: 3000 });
        return response?.data;
      } else {
        toast.error(response?.data?.message || "Failed to send invitation", { autoClose: 3000 });
        dispatch(slice.actions.hasError(response?.data?.message));
        return response?.data;
      }
    } catch (error) {
      console.error('INVITE USER ERROR', error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to send invitation", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}
